
import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion, useAnimation } from 'motion/react';
import { hapticFeedback } from '../../../utils/haptics';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullProgress, setPullProgress] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  const PULL_THRESHOLD = 80;

  const handleTouchStart = (e: React.TouchEvent) => {
    // Check if we are at the very top of the page (either body scroll or container scroll)
    const isAtTop = window.scrollY <= 0 || (containerRef.current && containerRef.current.scrollTop <= 0);
    
    if (isAtTop) {
      setStartY(e.touches[0].pageY);
    } else {
      setStartY(0);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (startY === 0 || isRefreshing) return;

    const currentY = e.touches[0].pageY;
    const diff = currentY - startY;

    if (diff > 0) {
      // Apply resistance
      const progress = Math.min(diff * 0.4, PULL_THRESHOLD + 20);
      setPullProgress(progress);
      
      if (progress >= PULL_THRESHOLD && pullProgress < PULL_THRESHOLD) {
        hapticFeedback.light();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (pullProgress >= PULL_THRESHOLD) {
      setIsRefreshing(true);
      setPullProgress(PULL_THRESHOLD);
      hapticFeedback.medium();
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullProgress(0);
      }
    } else {
      setPullProgress(0);
    }
    setStartY(0);
  };

  return (
    <div 
      ref={containerRef}
      className="relative h-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <motion.div
        className="absolute top-0 left-0 right-0 flex justify-center items-center pointer-events-none"
        style={{ 
          height: pullProgress, 
          opacity: Math.min(pullProgress / PULL_THRESHOLD, 1),
          zIndex: 40
        }}
        animate={{ height: isRefreshing ? 60 : pullProgress }}
      >
        <div className="bg-white rounded-full p-2 shadow-lg border border-emerald-50">
          <RefreshCw 
            className={`w-5 h-5 text-emerald-600 ${isRefreshing ? 'animate-spin' : ''}`} 
            style={{ transform: `rotate(${pullProgress * 2}deg)` }}
          />
        </div>
      </motion.div>
      
      <motion.div
        animate={{ y: isRefreshing ? 60 : pullProgress }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  );
}
